import { LevelSession } from "@highmarch/app";
import { BalanceData, Cell, Command, CommandKind, CommandResult, GearBonus, GearItem, LevelSim, ScriptedPlayer, StarMask } from "@highmarch/domain";
import type * as THREE from "three";
import { GestureSource } from "../input/gestures";
import type { GroundMesh } from "../render/groundMesh";
import { SceneRig } from "../render/scene";
import { Terrain } from "../render/terrain";
import type { EnemyViews } from "../render/views";
import type { CritterViews } from "../render/views/critters";
import { attachBossBar } from "../ui/bossBar";
import { CardHandView } from "../ui/cardHand";
import { EnemyCard } from "../ui/enemyCard";
import { HudView } from "../ui/hud";
import type { Readiness } from "../ui/hub/readiness";
import { RefusalFeedback } from "../ui/refusal";
import type { LevelEndChoice } from "../ui/screens";
import { TowerInfoView } from "../ui/towerInfo";
import { LevelMoments, showIntro } from "./announcer";
import { HitStop, TimeScales } from "./hitStop";
import { PendingRewards } from "./impactWaits";
import { LevelDebug } from "./levelDebug";
import { LevelEnd } from "./levelEnd";
import { LevelGuideHost } from "./levelGuideHost";
import { gestureHandlers, handHandlers, hudHandlers, undoable } from "./levelInput";
import { LevelLayout } from "./levelLayout";
import type { LevelParts } from "./levelParts";
import { LevelPause } from "./levelPause";
import { ScreenPicker } from "./levelPicker";
import { LevelRally } from "./levelRally";
import { LevelScene } from "./levelScene";
import { LevelSync } from "./levelSync";
import { LevelTaps } from "./levelTaps";
import { LevelTicks } from "./levelTicks";
import type { Moment } from "./moments";
import { PlacementCamera } from "./placementCamera";
import { SimClock } from "./simClock";
import { UpgradeAim } from "./upgradeAim";
import { EndScreenRender } from "./endScreenRender";
import { SnapshotCadence } from "./snapshotCadence";
import { frameJournal } from "../infra/frameJournal";
import { LevelUiScope } from "./levelUiScope";
import { RareMoment } from "./rareMoment";
import { eventRunTitle } from "../ui/runMode";
import { WyrmHooks, WyrmMoment } from "./wyrmMoment";
import { LevelPet } from "./levelPet";

/** A paused frame's time scales: no sim time, frozen unit animation and particles (the camera and scenery still move). */
const PAUSED_SCALES: TimeScales = { sim: 0, fx: 0 };

/**
 * Glue for one level: owns the LevelSession, steps the sim at a fixed 20 Hz from real time, renders with
 * interpolation, and maps gestures and UI presses to Application commands. No game rules here. The level's parts
 * (level/level*.ts) work through one shared LevelParts record; this builds them, runs the frame loop and disposes.
 */
export class LevelController {
  readonly session: LevelSession;
  /** The level's 3D world (level/levelScene.ts); exposed for the headless captures, which probe its views. */
  readonly scene: LevelScene;
  /** Hit-stop and slow-motion (art_review/vfx.md F8): scales how much real time reaches the sim, never the tick. */
  readonly hitStop = new HitStop();
  /**
   * JavaScript time per phase of the frame, summed since the perf overlay last read it: the sim ticks and their
   * per-tick view hooks, the HUD/tray sync after the sim moved, per-frame view updates (units, towers, occluder
   * fading, effects, camera), and submitting the render. The overlay divides by `frames`. What a frame costs beyond
   * their sum is the browser's own work and the GPU.
   */
  readonly phaseMs = { sim: 0, ui: 0, views: 0, render: 0, frames: 0 };
  /** Set by the game: opens the settings sheet from the HUD gear and returns it (the level pauses while it is open). */
  onSettings: (() => HTMLElement | void) | null = null;
  /** Set by the game: the sim is running (true) or paused / over (false), for the screen wake lock. */
  onAwake: ((on: boolean) => void) | null = null;
  /** At the level's end: banks the run's stars (sim/stars.ts) and returns the level's best before them, for NEW BEST. */
  onStars: ((run: StarMask) => StarMask) | null = null;
  /** Set by the game: a wave was cleared and the next has not begun, the natural point to save the battle (suspend). */
  onCheckpoint: (() => void) | null = null;
  /**
   * Set by the game: UNDO before the waves (D17) — the level is rebuilt around `session`, this one's log minus the
   * last card replayed to the same tick (app/undo.ts). Unset, no undo is offered.
   */
  onUndo: ((session: LevelSession) => void) | null = null;
  /**
   * Set by the game: the run is over having reached wave `reached`. It records the best and returns the best before
   * this run and, on a loss, the hub's readiness for this level: the defeat screen's lesson reads both.
   */
  onRunOver: ((reached: number, won: boolean) => { previousBest: number; readiness: Readiness | null } | null) | null = null;
  private readonly parts: LevelParts;
  private readonly camFollow: PlacementCamera;
  private readonly hud: HudView;
  private readonly enemyCard: EnemyCard;
  private readonly refusals: RefusalFeedback;
  private readonly towerInfo: TowerInfoView;
  private readonly hand: CardHandView;
  private readonly gestures: GestureSource;
  private readonly clock = new SimClock();
  private readonly moments: LevelMoments;
  private readonly rewards = new PendingRewards();
  private readonly picker: ScreenPicker;
  private readonly upgrade: UpgradeAim;
  private readonly taps: LevelTaps;
  private readonly rally: LevelRally;
  private readonly layout: LevelLayout;
  private readonly pause: LevelPause;
  private readonly guideHost: LevelGuideHost;
  private readonly tickReaders: LevelTicks;
  private readonly sync: LevelSync;
  private readonly end: LevelEnd;
  private readonly debug: LevelDebug;
  private lastTime = performance.now();
  private raf = 0;
  private disposed = false;
  /** M33: the battle's suspended copy is also saved at each stage start and every 20 s of play (snapshotCadence.ts). */
  private readonly snapshots = new SnapshotCadence(performance.now(), 0);
  /** The rig was asked to rest under the settled end screen (once: a refusal falls back to EndScreenRender's refreshes). */
  private restTried = false;
  /** M30 #11: the level stops re-drawing itself under the finished end screen. */
  private readonly endRender = new EndScreenRender();
  /** Level frame loops running in the page (one at most: a second would double every frame's cost). */
  static liveLoops = 0;
  /** Every node the level adds to the UI layer, taken away as a set however the teardown goes (levelUiScope.ts). */
  private readonly ui: LevelUiScope;
  /** M36: the rare spawn's moment and the IMMUNE popups (level/rareMoment.ts). */
  private readonly rare: RareMoment;
  /** M40: the Starfall Wyrm's moment (level/wyrmMoment.ts). */
  private readonly wyrm: WyrmMoment;
  /** The pet's chip on the HUD and its bark's "WOOF!" (level/levelPet.ts). */
  private readonly petHud: LevelPet;

  constructor(
    private readonly rig: SceneRig,
    private readonly uiRoot: HTMLElement,
    balance: BalanceData,
    level: number,
    seed: bigint,
    owned: readonly boolean[],
    towerLevels: readonly number[],
    firstClear: boolean,
    private readonly onEnded: (level: number, won: boolean, choice: LevelEndChoice) => void,
    gear: readonly GearBonus[] = [],
    openChest: (() => GearItem[]) | null = null,
    powerScore = 0,
    /** A suspended battle already replayed to where it stopped (main.ts resume): the level is built around it, paused. */
    resumed: LevelSession | null = null,
    /** Rebuilt for an undo (D17): the view stays where the player had it and the intro card does not play again. */
    rebuilt = false,
  ) {
    this.session = resumed ?? new LevelSession(balance, level, seed, owned, towerLevels, firstClear, gear);
    this.camFollow = new PlacementCamera(rig.iso);
    const sim = this.sim;
    this.camFollow.skipRoad(sim);                               // the opening hand leaves the view on the base
    const view = rebuilt ? rig.iso.viewState() : null;
    rig.frame(sim.grid);
    // The shadow map is re-rendered at BattleShadowHz, not every frame (RC1 perf): only the fortress and the towers cast
    // (idling, aiming), and panning, building or a boss still refresh it at once (SceneRig.setShadowUpdates).
    rig.setShadowUpdates("manual", SceneRig.BattleShadowHz);
    rig.animatedCasters = 1;
    if (view) rig.iso.restoreView(view); else rig.focusCell(sim.grid.base, 2.5);
    this.scene = new LevelScene(rig, sim, seed, level, this.hitStop, (x, y, n) => this.hud.flyCoins(x, y, n));
    // First the parts that add nothing to the page, then the rest in the order their DOM has always been added.
    this.ui = new LevelUiScope(uiRoot);
    const p = this.parts = this.partsRecord();
    this.picker = new ScreenPicker(rig, sim, this.scene);
    this.upgrade = new UpgradeAim(p); this.taps = new LevelTaps(p); this.rally = new LevelRally(p);
    this.guideHost = new LevelGuideHost(p); this.tickReaders = new LevelTicks(p); this.sync = new LevelSync(p); this.debug = new LevelDebug(p);
    this.end = new LevelEnd(p, {
      stars: (run) => this.onStars?.(run) ?? 0, runOver: (reached, won) => this.onRunOver?.(reached, won) ?? null,
      openChest, leave: (l, won, choice) => this.onEnded(l, won, choice),
    });
    // M21: an Endless run is not its ground's level; an Iron run says it is one.
    const title = sim.isEndless ? `Endless · ${sim.level.name}` : sim.isIron ? `Iron · Level ${sim.levelNumber}` : sim.isDaily ? `Daily Road · ${sim.level.name}` : sim.isChallenge ? `Challenge · ${sim.level.name}` : sim.isEventRun ? eventRunTitle(sim) : `Level ${sim.levelNumber} · ${sim.level.name}`;
    this.hud = new HudView(uiRoot, title, powerScore, hudHandlers(p));
    this.enemyCard = new EnemyCard(uiRoot);
    this.refusals = new RefusalFeedback(uiRoot);
    this.layout = new LevelLayout(p);                           // the off-screen markers
    this.clock.speed = this.hud.speed;                     // the speed the player last chose (Settings.speed)
    this.moments = new LevelMoments(this.hud, this.hitStop);
    this.rare = new RareMoment(sim, rig, this.scene, this.hud, this.hitStop, uiRoot);
    this.wyrm = new WyrmMoment(sim, rig, this.scene, this.hud, this.hitStop, uiRoot, (c) => this.execute(c));
    this.petHud = new LevelPet(sim, this.scene.pet, this.scene.wild, this.hud, rig, uiRoot, (s) => this.guideHost.raise(s));
    this.moments.onLastStand = () => this.guideHost.raise("lastStand");
    this.pause = new LevelPause(p, { settings: () => this.onSettings?.(), awake: (on) => this.onAwake?.(on), resuming: () => { this.lastTime = performance.now(); } });
    attachBossBar(this.scene.enemies, this.hud.bossBar, (id) => sim.balance.enemies.find((x) => x.id === id)?.name ?? "Boss");   // ui: DOM boss bar on the boss events
    this.towerInfo = new TowerInfoView(uiRoot, (c) => this.execute(c), () => this.afterSimChange(true));
    this.hand = new CardHandView(uiRoot, balance, handHandlers(p));
    this.towerInfo.onUpgrade = (id, card) => this.upgrade.useCardOn(id, card);
    this.towerInfo.damage = this.session.damageByTower;
    this.towerInfo.assists = this.session.assistsByTower;
    this.gestures = new GestureSource(rig.canvas, gestureHandlers(p));
    this.layout.watchPanels();
    this.guideHost.attach();
    this.ui.claim();
    this.afterSimChange(true);
    // A resumed battle's first frame: the unit views catch up with the replayed sim, and the board waits paused.
    if (resumed) { this.scene.catchUp(); this.pause.holdResumed(); }
    // The guide waits for the intro card on its own (HudView.busyReason "intro"), the old tutorial's hand-off made general.
    if (!rebuilt) void showIntro(this.hud, this.sim, level).then(() => { if (!this.disposed) this.afterSimChange(true); });
    LevelController.liveLoops++;
    if (LevelController.liveLoops > 1) console.warn(`[level] ${LevelController.liveLoops} level frame loops running at once`);
    this.raf = requestAnimationFrame(this.frame);
  }

  /** The shared record the level's parts work through (level/levelParts.ts); every member is read when it is used. */
  private partsRecord(): LevelParts {
    const c = this;
    return {
      session: this.session, rig: this.rig, uiRoot: this.uiRoot,
      get sim() { return c.sim; }, get scene() { return c.scene; }, get hud() { return c.hud; }, get hand() { return c.hand; },
      get towerInfo() { return c.towerInfo; }, get enemyCard() { return c.enemyCard; }, get camFollow() { return c.camFollow; },
      get picker() { return c.picker; }, get taps() { return c.taps; }, get upgrade() { return c.upgrade; }, get rally() { return c.rally; },
      get layout() { return c.layout; }, get pause() { return c.pause; }, get guide() { return c.guideHost; }, get clock() { return c.clock; },
      get moments() { return c.moments; }, get rewards() { return c.rewards; }, get ticks() { return c.tickReaders; }, get end() { return c.end; },
      get rare() { return c.rare; }, get wyrm() { return c.wyrm; }, get pet() { return c.petHud; },
      get onUndo() { return c.onUndo; }, get ended() { return c.end.ended; }, get disposed() { return c.disposed; },
      execute: (command) => this.execute(command),
      afterSimChange: (force, ticked) => this.afterSimChange(force, ticked),
      raise: (s) => this.guideHost.raise(s),
    };
  }

  get sim(): LevelSim { return this.session.sim; }
  /** The run's best moments so far, best first (at most three), for the share card. */
  get topMoments(): readonly Moment[] { return this.moments.top; }
  /** The views the headless captures probe: the generated island geometry, the units, the critters. */
  get ground(): GroundMesh { return this.scene.ground; }
  get enemies(): EnemyViews { return this.scene.enemies; }
  get critters(): CritterViews { return this.scene.critters; }
  get rigForDebug(): SceneRig { return this.rig; }
  /** The level's relief, for the headless captures. */
  get terrainForDebug(): Terrain | null { return Terrain.current; }
  /** The run is over (end screen up) or the level torn down: nothing left to suspend. */
  get isFinished(): boolean { return this.end.ended || this.disposed; }
  /** Whether the sim is held (the pause button, the page hidden, Settings open). */
  get paused(): boolean { return this.clock.paused; }

  /** Leave the level from the settings sheet: counts as a loss, back to the hub. */
  quit(): void { if (!this.end.shown) this.onEnded(this.sim.levelNumber, false, "levels"); }
  /** The HUD's pause button: pauses, or resumes a pause the player can lift. */
  togglePause(): void { this.pause.toggle(); }
  /** The page went to the background (main.ts): the level stays paused under the paused layer when the player is back. */
  autoPause(): void { this.pause.autoPause(); }

  /** Dev/debug (level/levelDebug.ts): issue a command directly (bypasses the UI). */
  debugExecute(command: Command): CommandResult { return this.debug.execute(command); }
  /** Dev/debug: let the scripted player make its next decision (or step one tick). Returns whether it acted. */
  debugAutoplay(player = new ScriptedPlayer()): boolean { return this.debug.autoplay(player); }
  /** Drafts road up to the level's road pace knee, for the headless level validator. */
  debugDraftFullRoad(): { drafted: number; target: number; stalled: boolean; over: boolean } { return this.debug.draftFullRoad(); }
  /** Dev only: stage one tower beside the road with nothing else built; the cell it went on, or null. */
  debugStageTower(archetype: number, tier = 3, along = 0.5, near: { x: number; y: number } | null = null): Cell | null {
    return this.debug.stageTower(archetype, tier, along, near);
  }
  /** M36: where the rare chest reveal reads the gear its chest banked (boot levelFlow → MetaFlow.rareGearNow). */
  set rareGear(fn: () => readonly GearItem[]) { this.rare.rareGear = fn; }
  /** Dev/captures (M36): the next stage to start is a rare stage (`sim.debugForceRareNextStage()`). */
  debugRare(): void { this.debug.rare(); }
  /** M40: the Starfall Wyrm's outside world — its reward, the paid retry, the gems, wearing its skin (boot levelFlow). */
  set wyrmHooks(h: WyrmHooks | null) { this.wyrm.hooks = h; }
  /** Dev/captures (M40, docs/BUILD.md): the next stage to start is the Starfall Wyrm's. */
  debugWyrm(): void { this.debug.wyrm(); }
  /** Dev/captures (M40): the living Wyrm's HP to `frac` of its full HP (0.02: it falls to the board; 1000: it cannot). */
  debugWyrmHp(frac: number): boolean { return this.debug.wyrmHp(frac); }
  /** Dev/captures (M40): no rally offers for the Wyrm (an open one is skipped). */
  debugWyrmNoRally(): void { this.debug.wyrmNoRally(); }
  /** Dev/captures (M40): answer the choice as the sheet's buttons do (`retry` pays the gems first). */
  debugWyrmChoose(kind: "retry" | "release"): true | string { return this.wyrm.choose(kind); }
  /** Dev only: one enemy at the portal, now. */
  debugSpawnOne(archetype: number, hpMult = 1, isBoss = false): void { this.sim.debugSpawn(archetype, hpMult, isBoss); }
  /** Dev/debug: advance the simulation by wall-clock seconds without waiting for animation frames. */
  debugAdvance(seconds: number, render = true): void { this.debug.advance(seconds, render); }
  /** Forces the pending view rebuild, for headless callers that never run a frame. */
  debugFlushViews(): void { this.scene.flush(); }
  /** The scene's major layers, for the perf overlay to hide one at a time and see what the frame rate is paying for. */
  debugLayers(): Record<string, THREE.Object3D[]> { return this.scene.layers(); }

  private frame = (now: number): void => {
    if (this.disposed) return;                                   // never re-armed once the level is gone
    // never negative: a resume stamps lastTime with performance.now(), which can be later than the next frame's own
    // timestamp (the frame's start), and a negative step ran the end screen's wait and the effects backwards
    const dt = Math.max(0, Math.min(SimClock.MaxFrameSeconds, (now - this.lastTime) / 1000));
    this.lastTime = now;
    const T = this.phaseMs, t0 = performance.now();
    let t1 = t0, t2 = t0, t3 = t0, ran = 0;
    // Hit-stop: the sim and the animation mixers crawl (5 %), particles slow (25 %), the camera stays live. The sim
    // still steps whole fixed ticks — it is simply fed less time for a moment, exactly as if frames arrived late.
    // Paused: the hit-stop clock holds too, and the sim gets no time at all.
    const k = this.clock.paused ? PAUSED_SCALES : this.hitStop.step(dt);
    const slow = this.clock.paused ? 1 : this.rare.slowScale(dt) * this.wyrm.slowScale(dt);   // M36/M40: the kill in slow motion
    const simDt = dt * k.sim * slow, fxDt = dt * k.fx * slow;
    if (!this.end.ended) {
      // M34: given the frame's real interval the clock's pacer holds back ticks that would keep the battle stuck at
      // 30 fps after one late frame (level/tickPacer.ts); it learns the costs from `observe` below
      const { ticks, checkpoint } = this.tickReaders.run(this.clock.take(simDt, dt));
      ran = ticks;
      this.moments.frame(dt, this.sim, this.clock.paused);
      t1 = performance.now();
      if (checkpoint && !this.sim.isOver) { this.onCheckpoint?.(); this.snapshots.saved(now); }
      else if (!this.sim.isOver && this.snapshots.due(now, this.sim.stage, this.clock.paused)) this.onCheckpoint?.();   // M33
      if (ticks > 0) this.afterSimChange(false, true);
      else this.guideHost.update();                              // a tip's clock and the queue's grace run on frames, not ticks
      t2 = performance.now();
      this.scene.update(dt, simDt, fxDt, this.clock.alpha, now);
      this.rare.frame(dt);
      this.wyrm.frame(dt);
      this.petHud.frame();
      if (this.sim.isOver && this.end.due(dt)) this.end.show();
      t3 = performance.now();
    }
    this.scene.flush();
    this.rig.update(dt);
    this.layout.frame();
    this.scene.router?.syncLights(this.rig.camera);              // VFX light slots, in view space of the final camera
    const t4 = performance.now();
    // M33: once the end screen has settled, one last frame is drawn and kept as a still, and the level's frame-only GPU
    // memory goes (SceneRig.rest) — before the end screen's 3D chest makes its own context (it waits for this)
    const restNow = this.end.shown && !this.restTried && !this.rig.resting && this.endRender.restDue(now);
    const draw = !this.rig.resting && (restNow || this.endRender.want(this.end.shown, now, this.canvasKey()));
    if (draw) this.rig.render();
    if (restNow) { this.restTried = true; if (this.rig.rest()) this.scene.rest(); }
    const t5 = performance.now();
    T.sim += t1 - t0; T.ui += t2 - t1; T.views += t3 - t2 + (t4 - t3); T.render += t5 - t4; T.frames++;
    this.clock.pacer.observe(ran, t1 - t0, t5 - t1);
    const label = this.end.shown ? `L${this.sim.levelNumber} end screen` : `L${this.sim.levelNumber} wave ${this.sim.stage}`;
    frameJournal.frame(now, t4 - t0, t5 - t4, draw ? this.rig.frameStats.calls : 0, label, ran, this.clock.pacer.dropped);
    this.raf = requestAnimationFrame(this.frame);
  };

  private execute(command: Command): CommandResult {
    const r = this.session.execute(command);
    if (r !== CommandResult.Ok) {
      if (import.meta.env.DEV) console.debug(`[level] command refused: ${CommandResult[r]}`);
      // a road card with no fit says so in the tray, where the thumb is (pick handler); the rest get the toast
      if (!(command.kind === CommandKind.PickCard && r === CommandResult.InvalidPlacement)) this.refusals.refused(command, r);
    } else this.tickReaders.afterCommand();
    this.afterSimChange(false);
    return r;
  }

  /**
   * Syncs the views and the HUD after the sim moved (level/levelSync.ts), then gives the guide the new facts. `ticked`
   * is the frame loop's call, after ticks whose view hooks already ran once per tick.
   */
  private afterSimChange(force: boolean, ticked = false): void {
    this.sync.apply(force, ticked, undoable(this.parts), this.debug.driving);
    this.guideHost.update();
  }

  /** What the end screen's frozen level must be re-drawn for: the canvas's size and the page coming back. */
  private canvasKey(): string {
    const c = this.rig.canvas;
    return `${c.width}x${c.height}|${typeof document !== "undefined" ? document.visibilityState : ""}`;
  }

  /**
   * Takes the level down: every part, each on its own (one that throws is logged and the rest still go), then every
   * node the level put in the UI layer, as a set (levelUiScope.ts). A second call only re-sweeps the UI layer.
   */
  dispose(): void {
    if (this.disposed) { this.ui.release(); return; }
    LevelController.liveLoops = Math.max(0, LevelController.liveLoops - 1);
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.ui.claim();                                       // the end screen and anything else added since the build
    const steps: readonly (readonly [string, () => void])[] = [
      ["pause", () => this.pause.dispose()],               // un-ducks the music and lets the wake lock go
      ["hitStop", () => this.hitStop.reset()],
      ["moments", () => this.moments.stop()],              // the music low-pass must not follow the player to the hub
      ["rare", () => this.rare.dispose()],                 // the night, the sparkles and the rare's DOM
      ["wyrm", () => this.wyrm.dispose()],                 // the Wyrm's night, sky and DOM
      ["gestures", () => this.gestures.dispose()],
      ["pet", () => this.petHud.dispose()],
      ["hud", () => this.hud.dispose()],
      ["towerInfo", () => this.towerInfo.dispose()],
      ["guide", () => this.guideHost.detach()],
      ["enemyCard", () => this.enemyCard.close()],
      ["refusals", () => this.refusals.dispose()],
      ["hand", () => this.hand.dispose()],
      ["end", () => this.end.dispose()],
      ["layout", () => this.layout.dispose()],
      ["scene", () => this.scene.dispose()],
      ["rest", () => this.rig.wake()],                     // the next screen draws live (it would wake the rig itself)
      ["shadows", () => { this.rig.setShadowUpdates("auto"); this.rig.animatedCasters = 0; }],
    ];
    for (const [name, step] of steps) {
      try { step(); } catch (err) { console.error(`[level] dispose: ${name} failed`, err); }
    }
    this.ui.release();
  }
}
